import {
	CALENDAR_END_HOUR,
	CALENDAR_HOUR_LABELS,
	CALENDAR_START_HOUR,
	durationToPx,
	formatHourLabel,
	HOUR_HEIGHT_PX,
	minutesToPx,
} from '@/features/account-availability/calendar-helpers'
import i18n from '@/i18n'
import type { PlaceableRegion, SelectedSlot } from './types'

export {
	CALENDAR_END_HOUR,
	CALENDAR_HOUR_LABELS,
	CALENDAR_START_HOUR,
	durationToPx,
	formatHourLabel,
	HOUR_HEIGHT_PX,
	minutesToPx,
}

export const SNAP_MINUTES = 5

export function snapToGrid(minutesFromMidnight: number): number {
	return Math.round(minutesFromMidnight / SNAP_MINUTES) * SNAP_MINUTES
}

export function pxToMinutes(px: number): number {
	return Math.round((px / HOUR_HEIGHT_PX) * 60) + CALENDAR_START_HOUR * 60
}

export function isWithinRegion(
	startMinutes: number,
	durationMinutes: number,
	regions: PlaceableRegion[],
	timezone: string,
): boolean {
	const endMinutes = startMinutes + durationMinutes
	return regions.some((region) => {
		const regionStartMinutes = dateToMinutesFromMidnight(region.placeableFrom, timezone)
		const regionEndMinutes = dateToMinutesFromMidnight(region.placeableTo, timezone)
		return startMinutes >= regionStartMinutes && endMinutes <= regionEndMinutes
	})
}

/**
 * Given where the cursor is (in candidate-local minutes from midnight), find the best
 * slot start that keeps the full duration inside a region.
 *
 * Ideal start is cursor - duration/2 (centered). We clamp it to [regionStart, regionEnd - duration]
 * so hovering near a region edge keeps the ghost visible rather than dismissing it.
 * Returns null when the cursor is outside every region or the region is shorter than the duration.
 */
export function findSlotStartInRegion(
	cursorMinutes: number,
	durationMinutes: number,
	regions: PlaceableRegion[],
	timezone: string,
): number | null {
	for (const region of regions) {
		const regionStart = dateToMinutesFromMidnight(region.placeableFrom, timezone)
		const regionEnd = dateToMinutesFromMidnight(region.placeableTo, timezone)
		if (cursorMinutes < regionStart || cursorMinutes > regionEnd) { continue }
		const maxStart = regionEnd - durationMinutes
		if (maxStart < regionStart) { continue }
		// Clamp against grid-aligned boundaries so snapToGrid can't push outside the valid range.
		const minGridStart = Math.ceil(regionStart / SNAP_MINUTES) * SNAP_MINUTES
		const maxGridStart = Math.floor(maxStart / SNAP_MINUTES) * SNAP_MINUTES
		if (maxGridStart < minGridStart) { continue }
		const idealStart = cursorMinutes - durationMinutes / 2
		const snapped = Math.max(minGridStart, Math.min(maxGridStart, snapToGrid(idealStart)))
		return snapped
	}
	return null
}

export function dateToMinutesFromMidnight(date: Date, timezone?: string): number {
	if (timezone) {
		const parts = new Intl.DateTimeFormat('en', {
			hour: 'numeric',
			minute: 'numeric',
			hour12: false,
			timeZone: timezone,
		}).formatToParts(date)
		const hour = Number(parts.find((part) => part.type === 'hour')?.value ?? 0)
		const minute = Number(parts.find((part) => part.type === 'minute')?.value ?? 0)
		return hour * 60 + minute
	}
	return date.getHours() * 60 + date.getMinutes()
}

export function getRegionsForDate(date: Date, regions: PlaceableRegion[], timezone: string): PlaceableRegion[] {
	const targetDateStr = formatDateInTimezone(date, timezone)
	return regions.filter((region) => {
		const regionDateStr = formatDateInTimezone(region.placeableFrom, timezone)
		return regionDateStr === targetDateStr
	})
}

export function formatDateInTimezone(date: Date, timezone: string): string {
	return new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(date)
}

export function slotToIso(slot: SelectedSlot): string {
	return slot.startsAt.toISOString()
}

export function parseRegions(
	raw: { placeable_from: string; placeable_to: string }[],
): PlaceableRegion[] {
	return raw.map((region) => ({
		placeableFrom: new Date(region.placeable_from),
		placeableTo: new Date(region.placeable_to),
	}))
}

export function getWeekStartingFrom(anchorDate: Date): Date[] {
	// Use UTC arithmetic so noon-UTC anchor dates stay stable across all timezones.
	const monday = new Date(anchorDate)
	const day = monday.getUTCDay()
	const diffToMonday = day === 0 ? -6 : 1 - day
	monday.setUTCDate(monday.getUTCDate() + diffToMonday)
	monday.setUTCHours(12, 0, 0, 0)
	return Array.from({ length: 7 }, (_, i) => {
		const date = new Date(monday)
		date.setUTCDate(monday.getUTCDate() + i)
		return date
	})
}

export function findFirstWeekWithRegions(regions: PlaceableRegion[], timezone: string): Date {
	if (regions.length === 0) {
		return new Date()
	}
	const firstRegionDate = regions.reduce(
		(earliest, region) => (region.placeableFrom < earliest ? region.placeableFrom : earliest),
		regions[0].placeableFrom,
	)
	// Noon UTC of the local date in candidate timezone — stable for all UTC±12 offsets
	// so formatDateInTimezone(date, candidateTimezone) always returns the intended date.
	return new Date(formatDateInTimezone(firstRegionDate, timezone) + 'T12:00:00Z')
}

export function formatTimeLabel(minutesFromMidnight: number, _timezone?: string): string {
	// minutesFromMidnight is already the local time in the candidate's timezone.
	// Encoding it as UTC hours and formatting in UTC yields the correct 12-hour label
	// regardless of the browser's own timezone.
	const date = new Date()
	date.setUTCHours(Math.floor(minutesFromMidnight / 60), minutesFromMidnight % 60, 0, 0)
	// Let the active locale decide 12h vs 24h rather than forcing AM/PM.
	return new Intl.DateTimeFormat(i18n.language, {
		hour: 'numeric',
		minute: '2-digit',
		timeZone: 'UTC',
	}).format(date)
}

export function formatSlotDisplay(slot: SelectedSlot, durationMinutes: number, timezone: string): string {
	const formatter = new Intl.DateTimeFormat(i18n.language, {
		weekday: 'short',
		month: 'short',
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		timeZone: timezone,
	})
	const endFormatter = new Intl.DateTimeFormat(i18n.language, {
		hour: 'numeric',
		minute: '2-digit',
		timeZone: timezone,
	})
	return `${formatter.format(slot.startsAt)} – ${endFormatter.format(slot.endsAt)}`
}
